import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import classNames from 'classnames';

import { ToastContext } from '@/components/common/CommonToast';
import { useLoading } from '@/components/common/loader/LoadingContext';
import ButtonCustom from '@/components/common/ui/button';
import { InputTextCustomForm } from '@/components/common/ui/input/Input';
import { useTranslation } from '@/config/i18n';
import { toastMessage } from '@/lib/utils';
import PATH from '@/routes/path';
import { apiRequest } from '@/services';
import { ACTION_TYPE } from '@/shared/constants/user';
import UserManagementModalConfirm from './UserManagementModalConfirm';
import { UserManagementType } from '@/types/user';

type Props = {
  id?: number;
};

const UserManagementForm: React.FC<Props> = ({ id }) => {
  const { toast } = useContext(ToastContext);
  const [t] = useTranslation('');
  const { apiService } = apiRequest();

  const navigate = useNavigate();
  const { loadingGlobal, setLoadingGlobal } = useLoading();
  const [dialogConfirmApproveVisible, setDialogConfirmApproveVisible] =
    useState(false);
  const [dialogConfirmRejectVisible, setDialogConfirmRejectVisible] =
    useState(false);
  const [user, setUser] = useState<UserManagementType>();

  const formMethods = useForm({
    mode: 'onChange',
    shouldFocusError: false,
  });

  const { reset, watch } = formMethods;

  function setDefaultValues(user: any) {
    reset({
      first_name_kanji: user.first_name_kanji,
      last_name_kanji: user.last_name_kanji,
      first_name_kana: user.first_name,
      last_name_kana: user.last_name,
      date_of_birth: dayjs(user.date_of_birth?.toString()).format('YYYY/MM/DD'),
      gender: t(`common.gender.${user.gender}`),
      email: user.email,
      phone_number: user.phone_number,
      post_code: user.post_code,
      graduation: user.graduation && user.graduation_term
        ? `${user.graduation.period_number}期ー${user.graduation_term?.name ?? ''}`
        : user.graduation?.period_number?.toString() ?? '',
    });
    setUser(user);
  }

  async function getUserDetail(id: number) {
    setLoadingGlobal(true);
    const { data: user } = await apiService.usersCoachRequest.show(id);

    if (user?.data) {
      setDefaultValues(user.data);
    }
    setLoadingGlobal(false);
  }

  function handleShowDialogConfirmApprove() {
    const nextDialogVisibleValue = !dialogConfirmApproveVisible;
    setDialogConfirmApproveVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;
  }

  function handleShowDialogConfirmReject() {
    const nextDialogVisibleValue = !dialogConfirmRejectVisible;
    setDialogConfirmRejectVisible(nextDialogVisibleValue);

    if (nextDialogVisibleValue) return;
  }

  function handleReject() {
    handleShowDialogConfirmReject();
  }

  function handleApprove() {
    handleShowDialogConfirmApprove();
  }

  function handleNavigateBack() {
    navigate(PATH.user_management);
  }

  async function handleChangeStatusUser(actionType: string) {
    if (!id) return;

    setLoadingGlobal(true);
    const { data } = await apiService.usersCoachRequest.create({
      ids: [id],
      action: actionType,
    });

    if (data?.data.length) {
      setLoadingGlobal(false);
      setDialogConfirmApproveVisible(false);
      setDialogConfirmRejectVisible(false);
      return;
    }

    const messageSuccess =
      actionType === ACTION_TYPE.APPROVE
        ? t('user_management.approve_success')
        : t('user_management.reject_success');
    toast?.current?.show(toastMessage(messageSuccess));

    setLoadingGlobal(false);
    handleNavigateBack();
  }

  useEffect(() => {
    if (id) {
      getUserDetail(id);
    }
  }, [id]);

  const graduationText = watch('graduation') ?? '';

  return (
    <>
      <FormProvider {...formMethods}>
        <form className="mt-[16px]" autoComplete="off">
          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.full_name_kanji')}</span>
            </div>
            <div className="w-[512px] grid grid-cols-2 gap-6">
              <div className='h-[43px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 py-[10px] px-[12px]'>
                <Tooltip target={`.tooltip-point-${user?.id}-last-name-kanji`}>
                  {user?.last_name_kanji}
                </Tooltip>
                <div
                  className={classNames(
                    'line-clamp-1 text-gray-700 break-all',
                    `tooltip-point-${user?.id}-last-name-kanji`,
                  )}
                  data-pr-position="top"
                >
                  {user?.last_name_kanji}
                </div>
              </div>

              <div className='h-[43px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 py-[10px] px-[12px]'>
                <Tooltip target={`.tooltip-point-${user?.id}-first-name-kanji`}>
                  {user?.first_name_kanji}
                </Tooltip>
                <div
                  className={classNames(
                    'line-clamp-1 text-gray-700 break-all',
                    `tooltip-point-${user?.id}-first-name-kanji`,
                  )}
                  data-pr-position="top"
                >
                  {user?.first_name_kanji}
                </div>
              </div>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.full_name_kana')}</span>
            </div>
            <div className="w-[512px] grid grid-cols-2 gap-6">
              <div className='h-[43px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 py-[10px] px-[12px]'>
                <Tooltip target={`.tooltip-point-${user?.id}-last-name-kana`}>
                  {user?.last_name}
                </Tooltip>
                <div
                  className={classNames(
                    'line-clamp-1 text-gray-700 break-all',
                    `tooltip-point-${user?.id}-last-name-kana`,
                  )}
                  data-pr-position="top"
                >
                  {user?.last_name}
                </div>
              </div>

              <div className='h-[43px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 py-[10px] px-[12px]'>
                <Tooltip target={`.tooltip-point-${user?.id}-first-name-kana`}>
                  {user?.first_name}
                </Tooltip>
                <div
                  className={classNames(
                    'line-clamp-1 text-gray-700 break-all',
                    `tooltip-point-${user?.id}-first-name-kana`,
                  )}
                  data-pr-position="top"
                >
                  {user?.first_name}
                </div>
              </div>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.date_of_birth')}</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm name="date_of_birth" disabled />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.gender')}</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm name="gender" disabled />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.email')}</span>
            </div>
            <div className="w-[512px]">
              {/* <InputTextCustomForm name="email" disabled /> */}
              <div className='h-[43px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 py-[10px] px-[12px]'>
                <Tooltip target={`.tooltip-point-${user?.id}-email`}>
                  {user?.email}
                </Tooltip>
                <div
                  className={classNames(
                    'line-clamp-1 text-gray-700 break-all',
                    `tooltip-point-${user?.id}-email`,
                  )}
                  data-pr-position="top"
                >
                  {user?.email}
                </div>
              </div>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.phone_number')}</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm name="phone_number" disabled />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.post_code')}</span>
            </div>
            <div className="w-[512px]">
              <InputTextCustomForm name="post_code" disabled />
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.address')}</span>
            </div>
            <div className='w-[512px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 py-[10px] px-[12px]'>
              {user?.address}
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.graduation')}</span>
            </div>
            <div className="w-[512px]">
              <div className="h-[43px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 py-[10px] px-[12px]">
                <Tooltip target={`.tooltip-point-${user?.id}-graduation`}>
                  {graduationText}
                </Tooltip>
                <div
                  className={classNames(
                    'line-clamp-1 text-gray-700 break-all',
                    `tooltip-point-${user?.id}-graduation`,
                  )}
                  data-pr-position="top"
                >
                  {graduationText}
                </div>
              </div>
            </div>
          </div>

          <div className="form-item flex mt-[20px]">
            <div className="label w-56 flex">
              <span>{t('user_management.plan')}</span>
            </div>
            <div className="w-[512px]">
              <div
                className="px-[10px] py-[14px] border border-gray-300 bg-[#f2f2f2] rounded-lg opacity-70 overflow-hidden"
              >
                <b>{user?.plan?.name} - {user?.plan?.price.toLocaleString()}円/</b>月（税込）・受講生管理可能人数 {user?.plan?.student_limit}人
              </div>
            </div>
          </div>

          <div className="action flex justify-end mt-[25px] border-t border-gray-200 py-[18px] gap-5">
            <ButtonCustom
              className="min-w-[140px] !border-error-200 !bg-error-50 !text-error-700 font-semibold"
              type="button"
              severity="secondary"
              onClick={() => handleReject()}
            >
              {t('user_management.reject_button')}
            </ButtonCustom>
            <ButtonCustom
              className="min-w-[140px] !border-success-300 !bg-success-50 !text-success-700 font-semibold"
              type="button"
              severity="secondary"
              onClick={() => handleApprove()}
            >
              {t('user_management.approve_button')}
            </ButtonCustom>
          </div>
        </form>
      </FormProvider>

      <UserManagementModalConfirm
        dialogConfirmEditVisible={dialogConfirmApproveVisible}
        setDialogConfirmEditVisible={setDialogConfirmApproveVisible}
        handleChangeStatusUser={handleChangeStatusUser}
        loadingGlobal={loadingGlobal}
        actionType={ACTION_TYPE.APPROVE}
        title={t('user_management.approve_confirm.title')}
        content={t('user_management.approve_confirm.content')}
      />

      <UserManagementModalConfirm
        dialogConfirmEditVisible={dialogConfirmRejectVisible}
        setDialogConfirmEditVisible={setDialogConfirmRejectVisible}
        handleChangeStatusUser={handleChangeStatusUser}
        loadingGlobal={loadingGlobal}
        actionType={ACTION_TYPE.REJECT}
        title={t('user_management.reject_confirm.title')}
        content={t('user_management.reject_confirm.content')}
      />
    </>
  );
};

export default UserManagementForm;
